import {
  billingStatusSchema,
  portalSessionResponseSchema,
  setupIntentResponseSchema,
} from "@/schemas/billing";
import type {
  BillingStatus,
  ConfirmSetupRequest,
  PortalSessionRequest,
  PortalSessionResponse,
  SetupIntentResponse,
} from "@/schemas/billing";

import { apiRequest } from "./apiUtils";

export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await apiRequest<unknown>("billing/status", { method: "GET" });
  return billingStatusSchema.parse(res);
}

/** Creates a SetupIntent (card + us_bank_account) for the Payment Element. */
export async function createBillingSetupIntent(): Promise<SetupIntentResponse> {
  const res = await apiRequest<unknown>("billing/setup-intent", {
    method: "POST",
  });
  return setupIntentResponseSchema.parse(res);
}

/**
 * Saves the confirmed SetupIntent's payment method as the default. If an
 * invoice is past due the backend also retries it.
 */
export async function confirmBillingSetup(
  body: ConfirmSetupRequest,
): Promise<BillingStatus> {
  const res = await apiRequest<unknown>("billing/confirm-setup", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return billingStatusSchema.parse(res);
}

/** Uses one of the limited past-due dismissals. 409 when none are left. */
export async function dismissPastDue(): Promise<BillingStatus> {
  const res = await apiRequest<unknown>("billing/dismiss-past-due", {
    method: "POST",
  });
  return billingStatusSchema.parse(res);
}

export async function retryBillingPayment(): Promise<BillingStatus> {
  const res = await apiRequest<unknown>("billing/retry-payment", {
    method: "POST",
  });
  return billingStatusSchema.parse(res);
}

/** Stripe Customer Portal session (invoices, receipts, payment methods). */
export async function createBillingPortalSession(
  body: PortalSessionRequest,
): Promise<PortalSessionResponse> {
  const res = await apiRequest<unknown>("billing/portal-session", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return portalSessionResponseSchema.parse(res);
}

/**
 * Resumes the subscription: undoes a pending cancel-at-period-end (no charge),
 * or — when it has ended — starts a new one at once with the saved payment
 * method (no trial, charged now). 409 PAYMENT_METHOD_REQUIRED / 402 PAYMENT_FAILED.
 */
export async function resumeBillingSubscription(): Promise<BillingStatus> {
  const res = await apiRequest<unknown>("billing/resume", { method: "POST" });
  return billingStatusSchema.parse(res);
}
